import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  Grid,
  Modal,
  QueryState,
  Select,
  Stack,
} from '@integra/ui';
import { useCanvaDesigns, useConnectorConfigs, useImportFromCanva } from '../api/hooks.js';
import type { CanvaDesignSummary } from '../api/types.js';

/**
 * Import from Canva (creative source, Phase 1). The INPUT side of the flagship
 * flow: pick a design a team already made in Canva and bring it in as a
 * high-resolution Asset the repurpose + campaign pipeline can fit to every
 * channel and distribute.
 *
 * All data access is through the hooks layer (§21.4) — this component performs
 * no HTTP of its own. Nothing here publishes: an imported design lands as a
 * draft Asset for review and repurposing.
 */

/** One selectable design tile: thumbnail, title, and an import action. */
function DesignTile({
  design,
  imported,
  importing,
  onImport,
}: {
  design: CanvaDesignSummary;
  imported: boolean;
  importing: boolean;
  onImport: () => void;
}) {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-edge bg-surface">
      <div className="flex aspect-video items-center justify-center overflow-hidden bg-surface-sunken">
        {design.thumbnailUrl !== undefined ? (
          // A Canva-hosted, publicly reachable preview — safe to render directly.
          <img
            src={design.thumbnailUrl}
            alt={design.title}
            className="h-full w-full object-contain"
          />
        ) : (
          <span className="text-xs text-ink-muted">No preview</span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <p className="line-clamp-2 text-sm font-semibold text-navy-700">{design.title}</p>
        <div className="mt-auto">
          {imported ? (
            <Badge tone="success" dot>
              Imported
            </Badge>
          ) : (
            <Button variant="secondary" onClick={onImport} loading={importing} disabled={importing}>
              Import
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function CanvaImportModal({
  workspaceId,
  open,
  onClose,
}: {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
}) {
  const configs = useConnectorConfigs(workspaceId);
  const canvaConfigs = (configs.data?.data ?? []).filter((c) => c.connectorKey === 'canva');

  const [configId, setConfigId] = useState('');
  const [cursor, setCursor] = useState('');
  // Designs accumulate across pages so "Load more" grows the grid rather than
  // replacing it. Keyed by design id so a re-fetched page never double-lists.
  const [designs, setDesigns] = useState<CanvaDesignSummary[]>([]);
  const [imported, setImported] = useState<Set<string>>(new Set());
  const [importingId, setImportingId] = useState<string | null>(null);

  // Default to the first Canva connection once configs load.
  useEffect(() => {
    if (configId === '' && canvaConfigs.length > 0 && canvaConfigs[0] !== undefined) {
      setConfigId(canvaConfigs[0].id);
    }
  }, [canvaConfigs, configId]);

  const designsQuery = useCanvaDesigns(workspaceId, configId, cursor);
  const importDesign = useImportFromCanva(workspaceId);

  // Fold each fetched page into the accumulated list.
  useEffect(() => {
    const page = designsQuery.data?.data;
    if (page === undefined) return;
    setDesigns((prev) => {
      const seen = new Set(prev.map((d) => d.designId));
      const fresh = page.items.filter((d) => !seen.has(d.designId));
      return fresh.length === 0 ? prev : [...prev, ...fresh];
    });
  }, [designsQuery.data]);

  // Switching connection resets the grid and paging.
  const chooseConfig = (id: string): void => {
    setConfigId(id);
    setCursor('');
    setDesigns([]);
    setImported(new Set());
  };

  const runImport = (designId: string): void => {
    setImportingId(designId);
    importDesign.mutate(
      { connectorConfigId: configId, designId },
      {
        onSuccess: () => setImported((prev) => new Set(prev).add(designId)),
        onSettled: () => setImportingId(null),
      },
    );
  };

  const nextCursor = designsQuery.data?.data.nextCursor ?? null;
  const importedCount = imported.size;

  return (
    <Modal
      open={open}
      title="Import from Canva"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {importedCount > 0 ? 'Done' : 'Cancel'}
          </Button>
        </>
      }
    >
      <Stack gap="md">
        <p className="text-sm text-ink-muted">
          Bring a design in as a high-resolution asset. It lands as a draft you can repurpose per
          channel and publish — nothing is posted here.
        </p>

        {configs.isLoading ? null : canvaConfigs.length === 0 ? (
          <EmptyState
            title="No Canva connection yet"
            description="Connect a Canva account on the Connectors page, then come back to import your designs."
            action={
              <Link
                to={`/w/${workspaceId}/connectors`}
                className="text-sm font-semibold text-navy-700 hover:underline"
              >
                Go to Connectors
              </Link>
            }
          />
        ) : (
          <>
            {canvaConfigs.length > 1 && (
              <Field label="Canva connection" htmlFor="canva-connection">
                <Select
                  id="canva-connection"
                  value={configId}
                  onChange={(e) => chooseConfig(e.target.value)}
                >
                  {canvaConfigs.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.displayName}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            {importDesign.error instanceof Error && (
              <Alert tone="error" title="Import failed">
                {importDesign.error.message}
              </Alert>
            )}

            <QueryState
              loading={designsQuery.isLoading && designs.length === 0}
              error={designsQuery.error}
              what="Canva designs"
            >
              {designs.length === 0 ? (
                <EmptyState
                  title="No designs found"
                  description="This Canva account has no designs to import yet."
                />
              ) : (
                <Stack gap="md">
                  <Grid min="sm">
                    {designs.map((design) => (
                      <DesignTile
                        key={design.designId}
                        design={design}
                        imported={imported.has(design.designId)}
                        importing={importingId === design.designId}
                        onImport={() => runImport(design.designId)}
                      />
                    ))}
                  </Grid>
                  {nextCursor !== null && (
                    <div className="flex justify-center">
                      <Button
                        variant="ghost"
                        onClick={() => setCursor(nextCursor)}
                        loading={designsQuery.isFetching}
                      >
                        Load more
                      </Button>
                    </div>
                  )}
                </Stack>
              )}
            </QueryState>
          </>
        )}
      </Stack>
    </Modal>
  );
}
